import { configureAuth } from "@playlive/fundraiser-auth/config";

/**
 * Wires the `@playlive/fundraiser-auth` singleton from this app's Vite env.
 *
 * ## Why the split base URIs
 *
 * Landing's Tiltify and Twitch OAuth lambdas are deployed to *different*
 * stacks:
 *
 * - Tiltify → the origin of `VITE_TILTIFY_REDIRECT_URI` (currently the
 *   `…api.dev.experience.stjude.org` host).
 * - Twitch  → `VITE_TWITCH_URI` (currently the prod
 *   `…api.experience.stjude.org` host).
 *
 * Collapsing them onto a single `apiBaseUri` would silently move one
 * provider to the other's environment, so we set both per-provider
 * overrides explicitly. `apiBaseUri` is the Twitch host purely as the
 * fallback for any future helper that doesn't name a provider.
 */
export function configurePlayLiveAuth(): void {
  configureAuth({
    apiBaseUri: twitchApiBaseUri(),
    tiltifyApiBaseUri: tiltifyApiBaseUri(),
    twitchApiBaseUri: twitchApiBaseUri(),
    tiltifyClientId: import.meta.env.VITE_TILTIFY_CLIENT_ID,
    twitchClientId: import.meta.env.VITE_TWITCH_CLIENT_ID || undefined,
    twitchRedirectURI: import.meta.env.VITE_TWITCH_REDIRECT_URI || undefined,
  });
}

/**
 * Origin of the stack hosting the Tiltify OAuth lambdas, derived from the
 * full redirect URI so we don't introduce a second env var that can drift
 * out of sync with it.
 *
 * @returns The origin, or an empty string when the env var is unset or
 *   unparseable (dev shells without a `.env`, some test runners).
 */
function tiltifyApiBaseUri(): string {
  try {
    return new URL(import.meta.env.VITE_TILTIFY_REDIRECT_URI).origin;
  } catch {
    return "";
  }
}

/** Origin of the stack hosting the Twitch OAuth lambdas. */
function twitchApiBaseUri(): string {
  return import.meta.env.VITE_TWITCH_URI ?? "";
}
